import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';

import {
  AnalyticsService,
  ZoneDeviationNotice,
} from '../../analytics/analytics.service';
import { ZoneDeviationDialogComponent } from '../zone-deviation-dialog/zone-deviation-dialog.component';

export type ZoneDeviationBannerMode = 'global' | 'contextual';

/**
 * Banner fuer Zonen-Drift-Hinweise.
 *
 * - mode='global' (Default): laed Notices per OnInit selbst und entscheidet
 *   anhand der Anzahl, ob ein Athlet- oder Coach-Banner gerendert wird.
 * - mode='contextual': erwartet bereits gefilterte Notices via [notices]-
 *   Input. Wird im Session-Detail / CP-Test-Detail genutzt.
 */
@Component({
  selector: 'app-zone-deviation-banner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterLink,
    MatIconModule,
    MatButtonModule,
    TranslateModule,
  ],
  template: `
    @if (visibleNotices.length > 0) {
      <div class="zd-banner" [class.zd-banner--coach]="visibleNotices.length > 1">
        <mat-icon class="zd-icon">warning</mat-icon>

        <div class="zd-text">
          @if (visibleNotices.length === 1; as _) {
            <strong>
              @if (visibleNotices[0].athlete_name && !isSelfNotice(visibleNotices[0])) {
                {{ 'diagnostics.zone_deviation.banner.title_athlete' | translate }} —
                {{ visibleNotices[0].athlete_name }}
              } @else {
                {{ 'diagnostics.zone_deviation.banner.title_athlete' | translate }}
              }
            </strong>
            <span class="zd-body">
              {{ 'diagnostics.zone_deviation.banner.body_athlete' | translate:{
                pct: absPct(visibleNotices[0].deviation_max_pct).toFixed(1)
              } }}
            </span>
          } @else {
            <strong>
              {{ 'diagnostics.zone_deviation.banner.title_coach' | translate:{ n: visibleNotices.length } }}
            </strong>
            <ul class="zd-list">
              @for (n of visibleNotices; track n.id) {
                <li>
                  <a (click)="openDialog(n)">
                    {{ n.athlete_name || ('User #' + n.user_id) }}
                    <small>({{ absPct(n.deviation_max_pct).toFixed(1) }}%)</small>
                  </a>
                </li>
              }
            </ul>
          }
          <a class="zd-settings-link"
             routerLink="/analyse/diagnostik/trainingsbereiche"
             [queryParams]="{ tab: 'einstellungen' }">
            {{ 'diagnostics.zone_deviation.banner.settings_link' | translate }}
          </a>
        </div>

        <span class="grow"></span>
        @if (visibleNotices.length === 1) {
          <button mat-button color="primary" (click)="openDialog(visibleNotices[0])">
            {{ 'diagnostics.zone_deviation.banner.cta' | translate }}
          </button>
        }
        <button mat-icon-button (click)="hideBanner()" aria-label="dismiss">
          <mat-icon>close</mat-icon>
        </button>
      </div>
    }
  `,
  styles: [`
    .zd-banner {
      display: flex;
      align-items: flex-start;
      gap: 0.6rem;
      padding: 0.6rem 0.85rem;
      margin: 0.5rem 0;
      background: #fff8e1;
      color: #8d6e00;
      border-left: 4px solid #ef6c00;
      border-radius: 6px;
      font-size: var(--fs-sm);
    }
    .zd-banner--coach { background: #e3f2fd; color: #0d47a1; border-left-color: #1565c0; }
    .zd-icon { color: #ef6c00; flex-shrink: 0; margin-top: 0.1rem; }
    .zd-banner--coach .zd-icon { color: #1565c0; }
    .zd-text { flex: 1; min-width: 0; }
    .zd-text strong { display: block; }
    .zd-body { display: block; margin-top: 0.15rem; color: inherit; }
    .zd-list { margin: 0.35rem 0 0; padding-left: 1.25rem; }
    .zd-list a { cursor: pointer; text-decoration: underline; color: inherit; }
    .zd-list small { color: var(--text-secondary, #888); margin-left: 0.25rem; }
    .zd-settings-link {
      display: inline-block; margin-top: 0.4rem;
      font-size: var(--fs-xs); color: inherit;
      text-decoration: underline; cursor: pointer;
    }
    .grow { flex: 1; }
  `],
})
export class ZoneDeviationBannerComponent implements OnInit {
  private readonly analytics = inject(AnalyticsService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() mode: ZoneDeviationBannerMode = 'global';
  @Input() notices: ZoneDeviationNotice[] | null = null;
  /** User-ID des aktuell eingeloggten Users — fuer "isSelfNotice"-Heuristik. */
  @Input() currentUserId: number | null = null;

  visibleNotices: ZoneDeviationNotice[] = [];
  private suppressed = false;

  ngOnInit(): void {
    if (this.mode === 'contextual') {
      this.visibleNotices = this.notices ?? [];
      return;
    }
    this.refresh();
  }

  private refresh(): void {
    this.analytics.getZoneDeviationNotices('open').subscribe({
      next: (resp) => {
        this.visibleNotices = this.suppressed ? [] : (resp.notices ?? []);
        this.cdr.markForCheck();
      },
      error: () => {
        this.visibleNotices = [];
        this.cdr.markForCheck();
      },
    });
  }

  hideBanner(): void {
    this.suppressed = true;
    this.visibleNotices = [];
    this.cdr.markForCheck();
  }

  openDialog(notice: ZoneDeviationNotice): void {
    const ref = this.dialog.open(ZoneDeviationDialogComponent, {
      data: { notice },
      width: '640px',
      maxWidth: '95vw',
    });
    ref.afterClosed().subscribe((result) => {
      if (result?.resolved) {
        // Notice wurde serverseitig resolved/dismissed — aus der Anzeige entfernen
        this.visibleNotices = this.visibleNotices.filter((n) => n.id !== notice.id);
        this.cdr.markForCheck();
      }
    });
  }

  absPct(v: number | null): number {
    return v == null ? 0 : Math.abs(v);
  }

  isSelfNotice(n: ZoneDeviationNotice): boolean {
    return this.currentUserId != null && n.user_id === this.currentUserId;
  }
}
